import React from 'react';
import { Link2, File, FileImage } from 'lucide-react';
import { cn } from '../lib/utils';
import { getResourceKind } from '../utils/resource-kind';
import { getUrlHostname } from '../utils/resource-display-utils';
import { splitFileName } from '../utils/text';

export interface ResourceTitleResource {
  title: string;
  url: string | null;
  s3Key: string | null;
  mimeType: string | null;
  fileName?: string | null;
  tags?: string[] | null;
}

export const ResourceTitle: React.FC<{
  resource: ResourceTitleResource;
  className?: string;
}> = ({ resource, className }) => {
  const kind = getResourceKind(resource);

  if (kind === 'link' && resource.url) {
    const hostname = getUrlHostname(resource.url);
    return (
      <a
        href={resource.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'inline-flex max-w-full items-start gap-1.5 break-words text-scribane-green hover:text-foreground hover:font-semibold hover:no-underline',
          className
        )}
        title={resource.url}
      >
        <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span className="flex min-w-0 flex-col">
          <span className="break-words">{resource.title}</span>
          {hostname && (
            <span className="truncate text-xs font-normal text-muted-foreground">{hostname}</span>
          )}
        </span>
      </a>
    );
  }

  if (kind === 'document') {
    const fileName = resource.fileName?.trim() || resource.title;
    const { base, extension } = splitFileName(fileName);
    return (
      <span
        className={cn(
          'mb-1 inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-foreground',
          className
        )}
      >
        <File className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="flex min-w-0 flex-1 items-center text-xs" title={fileName}>
          <span className="min-w-0 truncate whitespace-nowrap">{base}</span>
          {extension && <span className="shrink-0 whitespace-nowrap">{extension}</span>}
        </span>
      </span>
    );
  }

  if (kind === 'document-image') {
    return (
      <span className={cn('inline-flex max-w-full items-center gap-1.5', className)}>
        <FileImage className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="break-words">{resource.title}</span>
      </span>
    );
  }

  return <span className={cn('break-words', className)}>{resource.title}</span>;
};
